/** @typedef {Record<string, string>} FlatParams */

/** @typedef {{
 *   utm_mark: string;
 *   utm_params: FlatParams;
 *   client_id: string;
 *   client_sess_id: string;
 *   captured_at_iso: string;
 *   last_url_visit_iso?: string;
 *   from_google_ads: boolean;
 *   from_microsoft_ads: boolean;
 *   ga_property_cookie_name?: string;
 * }} AttributionSnapshot */

const DEFAULT_KEY = 'cv_attribution_v1';

/**
 * Serialize all query keys from current location (pathname + search = PHP REQUEST_URI analogue).
 */
export function readUrlSnapshot() {
    if (typeof window === 'undefined') {
        return { utm_mark: '', utm_params: {}, hasQuery: false, search: '', pathname: '' };
    }
    const pathname = window.location.pathname || '';
    const search = window.location.search || '';
    const utm_mark = pathname + search;
    const hasQuery = search.length > 1;
    /** @type {FlatParams} */
    const utm_params = {};
    if (hasQuery) {
        const sp = new URLSearchParams(search.slice(1));
        sp.forEach((v, k) => {
            utm_params[k] = v;
        });
    }
    return { utm_mark, utm_params, hasQuery, search, pathname };
}

/**
 * @param {string} [storageKey]
 * @returns {AttributionSnapshot|null}
 */
export function loadStored(storageKey = DEFAULT_KEY) {
    if (typeof localStorage === 'undefined') return null;
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return /** @type {AttributionSnapshot} */ (parsed);
    } catch {
        return null;
    }
}

/**
 * @param {AttributionSnapshot} snapshot
 * @param {string} [storageKey]
 */
export function saveStored(snapshot, storageKey = DEFAULT_KEY) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch (_) {
        // quota / privacy mode — ignore
    }
}

/** @returns {Partial<AttributionSnapshot>} merged layer */
export function mergeAttribution(previous, incoming) {
    /** @type {AttributionSnapshot} */
    const base = {
        utm_mark: previous?.utm_mark || '',
        utm_params: { ...(previous?.utm_params || {}) },
        client_id: previous?.client_id || '',
        client_sess_id: previous?.client_sess_id || '',
        captured_at_iso: previous?.captured_at_iso || new Date().toISOString(),
        from_google_ads: Boolean(previous?.from_google_ads),
        from_microsoft_ads: Boolean(previous?.from_microsoft_ads),
        ga_property_cookie_name: previous?.ga_property_cookie_name,
    };

    if (incoming.utm_mark) base.utm_mark = incoming.utm_mark;
    if (incoming.utm_params && Object.keys(incoming.utm_params).length > 0) {
        base.utm_params = { ...base.utm_params, ...incoming.utm_params };
        base.last_url_visit_iso = new Date().toISOString();
    }
    if (incoming.client_id) base.client_id = incoming.client_id;
    if (incoming.client_sess_id) base.client_sess_id = incoming.client_sess_id;
    if (typeof incoming.from_google_ads === 'boolean') base.from_google_ads = incoming.from_google_ads;
    if (typeof incoming.from_microsoft_ads === 'boolean') base.from_microsoft_ads = incoming.from_microsoft_ads;
    if (incoming.ga_property_cookie_name) base.ga_property_cookie_name = incoming.ga_property_cookie_name;

    base.captured_at_iso = incoming.captured_at_iso || base.captured_at_iso;
    return base;
}

export const STORAGE_KEY = DEFAULT_KEY;
